import React, { useEffect, useMemo } from 'react';
import { Tooltip } from 'antd';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { WarningOutlined } from '@ant-design/icons';
import { percentileConfig } from '@utils/metrics/constants';
import { percentileAtom } from '@components/metrics-panel-hz/atoms';
import { intervalSelector } from '@store/common/selectors';
import { getAllowedPercentile } from '@utils/percentile/utils';
import { PercentileSelectorWrapper, StyledPercentileSelect, WarningIcon } from './styled';

function PercentileSelector() {
  const [percentile, setPercentile] = useAtom(percentileAtom);
  const interval = useSelector(intervalSelector);

  const allowedValues = useMemo(() => getAllowedPercentile(interval), [interval]);
  const options = allowedValues.map((key) => percentileConfig[key]);
  const invalidValue = !allowedValues.includes(percentile);
  const isRestricted = allowedValues.length === 1 && allowedValues[0] === 'avg';

  // Automatic fallback: if current percentile is not allowed, fallback to 'avg'
  useEffect(() => {
    if (invalidValue && percentile !== 'avg') {
      setPercentile('avg');
    }
  }, [invalidValue, percentile, setPercentile]);

  return (
    <PercentileSelectorWrapper>
      <Tooltip title="Percentiles are supported only for date ranges up to 7 days. For longer periods, only Average is available.">
        <StyledPercentileSelect
          value={percentile}
          onChange={setPercentile}
          aria-label="Select percentile"
          disabled={isRestricted}
        >
          {options.map((config) => (
            <StyledPercentileSelect.Option key={config.percentile} value={config.percentile}>
              {config.short}
              {config.percentile !== 'avg'}
            </StyledPercentileSelect.Option>
          ))}
        </StyledPercentileSelect>
      </Tooltip>
      {invalidValue && (
        <Tooltip title="Selected percentile is invalid for the applied interval">
          <WarningIcon>
            <WarningOutlined />
          </WarningIcon>
        </Tooltip>
      )}
    </PercentileSelectorWrapper>
  );
}

export default PercentileSelector;
